/* Quantity sets: local selection only; pricing remains Shopify's authority. */
(() => {
const OCSetCore = Object.freeze({
  quote(prices, eligible, currency, count = prices.length, fixedOnly = false, twoRate = 20, threeRate = 25) {
    const repeat = prices.length === 1 ? count : 1;
    const original = prices.reduce((sum, price) => sum + price, 0) * repeat;
    if (!eligible) return { original, total: original, saved: 0, rate: 0, percent: 0 };
    const rate = count >= 3 ? threeRate : count >= 2 ? twoRate : 0;
    // Shopify floors each unit's discount to a minor unit (verified against Ajax).
    const percentageSaving = prices.reduce((sum, price) => sum + Math.floor(price * rate / 100), 0) * repeat;
    const fixedSaving = currency === 'USD' ? Math.min(500, original) : 0;
    const saved = Math.max(percentageSaving, fixedSaving);
    return {
      original,
      total: original - saved,
      saved,
      rate: saved === percentageSaving ? rate : 0,
      percent: original ? Math.round(saved * 100 / original) : 0
    };
  },
  merge(items) {
    const grouped = new Map();
    for (const item of items) {
      const properties = Object.fromEntries(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)));
      const key = JSON.stringify([item.id, properties]);
      if (grouped.has(key)) grouped.get(key).quantity += item.quantity;
      else grouped.set(key, { ...item, properties });
    }
    return [...grouped.values()];
  },
  validate(items, variants) {
    const counts = new Map();
    for (const item of items) counts.set(String(item.id), (counts.get(String(item.id)) || 0) + item.quantity);
    for (const [id, quantity] of counts) {
      const variant = variants.find(v => String(v.id) === id);
      if (!variant?.available) return 'Please choose an available option for every item.';
      if (quantity < variant.min || quantity % variant.step !== 0 || (variant.max !== null && quantity > variant.max)) return 'Please check the quantity requirements for your selected options.';
      if (variant.stock !== null && quantity + (variant.cartQty || 0) > variant.stock) return 'Not enough stock for this selection. Please reduce the quantity or choose another option.';
    }
    return '';
  }
});

if (typeof customElements !== 'undefined' && !customElements.get('orocuff-quantity-cards')) {
  customElements.define('orocuff-quantity-cards', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      try { this.model = JSON.parse(this.querySelector('[data-set-model]').textContent); } catch { return; }
      this.model.twoRate = Number(this.model.twoRate ?? 20);
      this.model.threeRate = Number(this.model.threeRate ?? 25);
      this.purchase = this.closest('.oc-purchase');
      this.form = document.getElementById(this.dataset.formId);
      this.directForm = document.getElementById(`product-direct-checkout-${this.dataset.sectionId}`);
      this.input = this.form?.querySelector('input[name="quantity"]');
      this.primaryId = this.form?.querySelector('input[name="id"]');
      if (!this.purchase || !this.form || !this.input || !this.primaryId || !this.model.variants.length) return;
      this.initialized = true;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.cards = [...this.querySelectorAll('[data-set-card]')];
      this.units = [];
      this.currencyFormat = new Intl.NumberFormat(document.documentElement.lang || 'en', { style:'currency', currency:this.model.currency });
      this.picker = this.purchase.querySelector('variant-radios,variant-selects');
      // Keep this product's primary center-design picker in Step 1. Multiple
      // items receive their own center-design controls inside Step 3.
      // The timeline's image-based Edition picker needs the full buy-box width.
      // Keep it above the set cards instead of squeezing it into a quantity card.
      if (this.picker && !this.model.timeline && !this.model.luggage && !this.model.fiveVerse && !this.model.giraffe) this.querySelector('[data-primary-picker-slot]').append(this.picker);
      this.quantityControl = this.purchase.querySelector('.oc-purchase__quantity');
      this.originalLuggage = this.form.querySelector('orocuff-luggage-options');
      this.originalCustomCenter = this.form.querySelector('orocuff-bracelet-custom-center');
      this.purchase.addEventListener('ocFivePersonalizationChanged', () => { this.syncPrices(); this.syncNotice(); this.syncSticky(); }, opts);
      this.purchase.addEventListener('ocFiveBadgeChanged', () => { this.syncNotice(); this.syncSticky(); }, opts);
      this.purchase.addEventListener('change', event => {
        if(event.target.matches('[data-oc-five-badge]')) { this.syncNotice(); this.syncSticky(); }
      }, opts);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-set-choice]')) {
          this.input.value = event.target.value;
          this.input.dispatchEvent(new Event('change', { bubbles:true }));
        } else if (event.target.matches('[data-set-option]')) {
          const row = event.target.closest('[data-set-unit]');
          const index = Number(row.dataset.setUnit);
          this.units[index].options[Number(event.target.dataset.setOption)] = event.target.value;
          this.units[index].id = this.resolve(this.units[index].options)?.id || null;
          if (this.model.customCenter) this.syncBraceletPersonalization(row,this.units[index],index);
          if (this.model.luggage) this.syncArtwork(row,this.units[index],index);
          this.syncPrices();
          this.syncNotice();
          this.syncSticky();
        } else if (event.target.matches('[data-set-artwork-file]')) {
          const row = event.target.closest('[data-set-unit]');
          const state = this.units[Number(row.dataset.setUnit)];
          state.artwork = event.target.files[0] || null;
          state.rights = false;
          this.syncArtwork(row,state,Number(row.dataset.setUnit));
        } else if (event.target.matches('[data-set-artwork-rights]')) {
          const row = event.target.closest('[data-set-unit]');
          this.units[Number(row.dataset.setUnit)].rights = event.target.checked;
        } else if (event.target.matches('[data-set-five-badge]')) {
          const row = event.target.closest('[data-set-unit]');
          const state = this.units[Number(row.dataset.setUnit)];
          state.fiveVerse.badge = this.model.badges.find((badge) => badge.key === event.target.value) || this.model.badges[0];
          this.syncNotice();
        } else if (event.target.matches('[data-set-five-personalized]')) {
          const row = event.target.closest('[data-set-unit]');
          const state = this.units[Number(row.dataset.setUnit)];
          state.fiveVerse.personalized = event.target.value === 'yes';
          this.syncFiveVersePersonalization(row, state, Number(row.dataset.setUnit));
          this.syncNotice();
        }
      }, opts);
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-set-property],[data-set-notes],[data-set-five-name]')) return;
        const index = Number(event.target.closest('[data-set-unit]').dataset.setUnit);
        if (event.target.matches('[data-set-five-name]')) {
          const value = window.OROCUFFFiveVerse?.sanitizeName?.(event.target.value) || event.target.value;
          if (event.target.value !== value) event.target.value = value;
          this.units[index].fiveVerse.name = value;
          this.syncNotice();
          return;
        }
        if (event.target.matches('[data-set-property]')) {
          const property=event.target.dataset.setProperty;
          const value=this.model.customCenter
            ? (property==='Left Name'
              ? event.target.value.toUpperCase().replace(/[^A-Z]/g,'').slice(0,8)
              : event.target.value.replace(/[^0-9]/g,'').slice(0,8))
            : event.target.value.replace(/[\r\n\t]/g,' ').slice(0,20);
          if(event.target.value!==value) event.target.value=value;
          this.units[index].properties[property]=value;
          this.syncNotice();
        }
        if (event.target.matches('[data-set-notes]')) this.units[index].notes = event.target.value.slice(0,500);
      }, opts);
      this.querySelector('[data-set-more]').addEventListener('click', () => {
        const button = this.querySelector('[data-set-more]');
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        this.quantityControl.dataset.setHidden = String(!expanded);
        if (expanded) this.input.focus();
      }, opts);
      this.input.addEventListener('change', () => this.sync(true), opts);
      this.primaryId.addEventListener('change', () => this.syncPrimary(), opts);
      this.originalLuggage?.addEventListener('change', event => {
        if (event.target.matches('[data-oclt-mode]')) this.sync(true);
      }, opts);
      document.addEventListener('afterVariantChanged', event => {
        if (String(event.detail?.sectionId) === this.dataset.sectionId) this.syncPrimary();
      }, opts);
      // The native theme resets quantity on variant/cart refresh. Keep an
      // explicit set selection, while still validating the new variant rules.
      document.addEventListener('afterQuantityUpdate', () => {
        const selected = Number(this.form.dataset.selectedQuantity) || this.quantity;
        const next = Math.max(selected,this.primaryVariant?.min || 1);
        if (this.quantity !== next) { this.input.value=String(next); this.sync(true); }
      }, opts);
      // Sticky quantities have their own native input; keep the set authoritative.
      document.addEventListener('change', event => {
        const sticky = event.target.closest('sticky-cart');
        if (sticky?.dataset.form !== this.dataset.formId || event.target.name !== 'quantity') return;
        this.input.value = event.target.value;
        this.input.dispatchEvent(new Event('change', { bubbles:true }));
      }, opts);
      this.quantityControl.dataset.setHidden = 'true';
      this.hidden = false;
      this.sync(true);
    }
    disconnectedCallback() { this.controller?.abort(); }
    get quantity() { const parsed=parseInt(this.input?.value,10); return Number.isSafeInteger(parsed) && parsed>0 ? parsed : 1; }
    get primaryVariant() { return this.model.variants.find(v => String(v.id) === String(this.primaryId.value)) || this.model.variants.find(v => String(v.id) === String(this.model.defaultVariant)); }
    money(cents) { return this.currencyFormat.format(cents / 100); }
    resolve(options) { return this.model.variants.find(v => v.options.every((value,index) => value === options[index])); }
    fiveVerseVariant(baseId, badge) {
      const base=this.model.variants.find(v=>String(v.id)===String(baseId)) || this.primaryVariant;
      if(!base) return null;
      const baseSize=String(base.options[1] || '').split(' :: ')[0];
      const desiredSize=badge?.variantSuffix ? `${baseSize} :: ${badge.variantSuffix}` : baseSize;
      return this.model.variants.find(v=>v.options[0]===base.options[0] && v.options[1]===desiredSize && v.options[2]===base.options[2]) || base;
    }
    usesMixedItems() { return this.model.mixed && this.quantity > 1 && this.quantity <= 20; }
    usesPerItemFormData() {
      return this.model.luggage && this.usesMixedItems() && this.units.slice(0,this.quantity).some(state => this.isCustom(state) && state.artwork);
    }
    ensureUnits(count) {
      const variant = this.primaryVariant;
      while (this.units.length < count) this.units.push({
        id:variant.id,
        options:variant.options.map((value,index)=>this.model.fiveVerse && index===1 ? String(value).split(' :: ')[0] : value),
        properties:{},
        notes:'',
        artwork:null,
        rights:false,
        fiveVerse:{ badge:{...(this.model.badges?.[0] || {key:'original',style:'Scripture Cross',metal:'Antique Silver',internalId:''})}, personalized:false, name:'' }
      });
    }
    trimUnits(count) {
      if (this.units.length <= count) return;
      this.units.splice(count);
    }
    syncPrimary() {
      const variant = this.primaryVariant;
      if (!variant) return;
      // Variant transitions replace the native picker with a new DOM element.
      this.picker = this.purchase.querySelector('variant-radios,variant-selects');
      if (this.quantity === 1) this.units = [];
      this.sync(this.quantity === 1);
    }
    sync(render) {
      this.input.value=String(this.quantity);
      const directQuantity = this.directForm?.querySelector('input[name="quantity"]');
      if (directQuantity) directQuantity.value=String(this.quantity);
      if (this.lastQuantity && this.quantity < this.lastQuantity) this.trimUnits(this.quantity);
      this.ensureUnits(Math.min(20, Math.max(3,this.quantity)));
      this.lastQuantity=this.quantity;
      const active = Math.min(3,this.quantity);
      const giraffeConfiguration = this.model.giraffe ? this.querySelector('[data-set-configuration]') : null;
      if (giraffeConfiguration && render) {
        giraffeConfiguration.hidden = true;
        giraffeConfiguration.replaceChildren();
      }
      for (const card of this.cards) {
        const count = Number(card.dataset.setCard);
        card.classList.toggle('is-selected', count === active);
        card.querySelector('[data-set-choice]').checked = count === active;
        card.querySelector('[data-set-body]').hidden = this.model.giraffe || count !== active;
        if (render && count > 1) this.renderUnits(card, count === 3 ? Math.max(3,this.quantity) : count);
        if (this.model.giraffe && count === active && count > 1 && giraffeConfiguration) {
          if (render) {
            const units = card.querySelector('[data-set-units]');
            giraffeConfiguration.replaceChildren(...units.childNodes);
          }
          giraffeConfiguration.hidden = false;
        }
      }
      if (this.picker) {
        if (!this.model.timeline && !this.model.luggage && !this.model.fiveVerse && !this.model.giraffe) {
          const target = active === 1 || this.usesMixedItems() ? this.querySelector('[data-primary-picker-slot]') : this.cards.find(card => Number(card.dataset.setCard) === active).querySelector('[data-set-units]');
          if (this.picker.parentElement !== target) target.prepend(this.picker);
        }
        this.picker.dataset.setHidden=String((this.model.luggage || this.model.giraffe) && this.usesMixedItems());
      }
      this.originalLuggage?.setMultiMode?.(this.model.luggage && this.usesMixedItems());
      this.originalCustomCenter?.setMultiMode?.(this.model.customCenter && this.usesMixedItems());
      this.purchase.ocFiveVerse?.setMultiMode?.(this.model.fiveVerse && this.usesMixedItems());
      const dynamic = this.purchase.querySelector('.oc-purchase__dynamic');
      if (dynamic) dynamic.dataset.setHidden = String(this.usesMixedItems());
      this.form.dataset.selectedQuantity = String(this.quantity);
      this.syncPrices(); this.syncNotice(); this.syncSticky();
    }
    renderUnits(card,count) {
      const container = card.querySelector('[data-set-units]');
      container.replaceChildren();
      if (this.model.mixed && !this.model.luggage && this.model.options.length === 1 && this.model.options[0] === 'Title') return;
      if (!this.model.mixed || count > 20) {
        const note=document.createElement('p'); note.className='oc-set__note';
        note.textContent='The choices below apply to every item in this set.';
        container.append(note);
        // Keep the native variant picker visible for add-ons/subscription/large sets.
        return;
      }
      for (let index=0; index<count; index++) {
        const state=this.units[index];
        const row=document.createElement('div'); row.className='oc-set__unit'; row.dataset.setUnit=String(index);
        const title=document.createElement('strong'); title.className='oc-set__unit-title'; title.textContent=`Item ${index+1}`;
        row.append(title);
        const fields=document.createElement('div'); fields.className='oc-set__fields'; row.append(fields);
        if (!(this.model.options.length === 1 && this.model.options[0] === 'Title')) {
          this.model.options.forEach((name,optionIndex) => {
            const values=[...new Set(this.model.variants.map(v=>v.options[optionIndex]))];
            if(values.length === 1) return;
            const label=document.createElement('label'); label.className='oc-set__field';
            if (this.model.options.length === 1 || /scripture|verse|design/i.test(name)) label.classList.add('is-wide');
            const caption=document.createElement('span'); caption.textContent=name;
            const select=document.createElement('select'); select.dataset.setOption=String(optionIndex);
            select.setAttribute('aria-label',`Item ${index+1} ${name}`);
            for (const value of values) {
              if(this.model.fiveVerse && optionIndex===1 && String(value).includes(' :: ')) continue;
              const option=document.createElement('option'); option.value=value; option.textContent=value;
              option.selected=value === state.options[optionIndex]; select.append(option);
            }
            label.append(caption,select); fields.append(label);
            if (this.model.fiveVerse && !this.model.raisedDome && optionIndex === 1) this.renderFiveVerseBadge(fields,state,index);
          });
        }
        if (this.model.fiveVerse) {
          this.renderFiveVersePersonalization(row,state,index,fields);
          this.syncFiveVersePersonalization(row,state,index);
        }
        if (this.model.luggage) {
          this.renderPersonalization(row,state,index);
        }
        if (this.model.luggage) {
          this.renderArtwork(row,state,index);
          this.syncArtwork(row,state,index);
        }
        if (this.model.customCenter) {
          this.renderBraceletPersonalization(row,state,index);
          this.syncBraceletPersonalization(row,state,index);
        }
        container.append(row);
      }
    }
    isCustom(state) {
      return state.options.some(value => /custom center artwork/i.test(value));
    }
    isBraceletPersonalized(state) {
      return this.originalCustomCenter?.isPersonalizedVariantId?.(state.id) ||
        state.options.some(value => /add personalization/i.test(value));
    }
    renderPersonalization(row,state,index) {
      const personal=document.createElement('div'); personal.className='oc-set__personal';
      const fields=document.createElement('div'); fields.className='oc-set__fields'; fields.dataset.setPersonalFields=''; fields.hidden=!this.originalLuggage?.personalized?.();
      for(const name of ['Name','Phone Number']) {
        const field=document.createElement('label'); field.className='oc-set__field';
        const caption=document.createElement('span'); caption.textContent=`${name} · Optional · up to 20 characters`;
        const input=document.createElement('input'); input.type=name==='Name'?'text':'tel'; input.maxLength=20; input.autocomplete='off';
        input.setAttribute('aria-label',`Item ${index+1} ${name}`); input.dataset.setProperty=name; input.value=state.properties[name] || '';
        field.append(caption,input); fields.append(field);
      }
      personal.append(fields); row.append(personal);
    }
    renderFiveVerseBadge(fields,state,index) {
      const label=document.createElement('label'); label.className='oc-set__field is-wide oc-set__five-badge';
      const caption=document.createElement('span'); caption.textContent='Cross Design';
      const select=document.createElement('select'); select.dataset.setFiveBadge='';
      select.setAttribute('aria-label',`Item ${index+1} Cross Design`);
      for(const badge of this.model.badges || []) {
        const option=document.createElement('option'); option.value=badge.key; option.textContent=`${badge.style} — ${badge.metal}`;
        option.selected=badge.key === state.fiveVerse.badge.key; select.append(option);
      }
      label.append(caption,select); fields.append(label);
    }
    renderFiveVersePersonalization(row,state,index,fields) {
      const wrapper=document.createElement('div'); wrapper.className='oc-set__personal oc-set__five-personal';
      const choice=document.createElement('label'); choice.className='oc-set__field is-wide';
      const caption=document.createElement('span'); caption.textContent='Personalization';
      const select=document.createElement('select'); select.dataset.setFivePersonalized='';
      select.setAttribute('aria-label',`Item ${index+1} Personalization`);
      for(const [value,label] of [['no','No Personalization — Free'],['yes','Add Name Embossing — +$7.99']]) {
        const option=document.createElement('option'); option.value=value; option.textContent=label;
        option.selected=(value==='yes') === state.fiveVerse.personalized; select.append(option);
      }
      choice.append(caption,select); wrapper.append(choice);
      const nameFields=document.createElement('div'); nameFields.className='oc-set__five-personal-fields'; nameFields.dataset.setFiveNameFields='';
      const nameLabel=document.createElement('label'); nameLabel.className='oc-set__field is-wide';
      const nameCaption=document.createElement('span'); nameCaption.textContent='Name or Initials · A–Z, 0–9, single spaces · max 12';
      const input=document.createElement('input'); input.type='text'; input.maxLength=12; input.autocomplete='off'; input.autocapitalize='characters'; input.spellcheck=false;
      input.placeholder='JORDAN 2026'; input.dataset.setFiveName=''; input.value=state.fiveVerse.name || '';
      input.setAttribute('aria-label',`Item ${index+1} Name or Initials`);
      nameLabel.append(nameCaption,input); nameFields.append(nameLabel);
      const note=document.createElement('p'); note.className='oc-set__note'; note.textContent='Embossed to the left of the Cross with 3.4 × 3.4 mm production dies.';
      nameFields.append(note); wrapper.append(nameFields); fields.append(wrapper);
    }
    syncFiveVersePersonalization(row,state,index) {
      const fields=row.querySelector('[data-set-five-name-fields]');
      if(!fields) return;
      fields.hidden=!state.fiveVerse.personalized;
      const input=fields.querySelector('[data-set-five-name]');
      input.disabled=!state.fiveVerse.personalized;
      if(!state.fiveVerse.personalized) { state.fiveVerse.name=''; input.value=''; input.setCustomValidity(''); }
      input.setAttribute('aria-label',`Item ${index+1} Name or Initials`);
    }
    renderBraceletPersonalization(row,state,index) {
      const personal=document.createElement('div'); personal.className='oc-set__personal';
      const fields=document.createElement('div'); fields.className='oc-set__fields'; fields.dataset.setBraceletPersonalFields='';
      for(const name of ['Left Name','Right Number']) {
        const field=document.createElement('label'); field.className='oc-set__field';
        const caption=document.createElement('span'); caption.textContent=name==='Left Name'
          ? 'Left — English Name · A–Z only · max 8'
          : 'Right — Number · 0–9 only · max 8';
        const input=document.createElement('input'); input.type='text'; input.maxLength=8; input.autocomplete='off';
        input.inputMode=name==='Left Name'?'text':'numeric'; input.pattern=name==='Left Name'?'[A-Z]{1,8}':'[0-9]{1,8}';
        input.placeholder=name==='Left Name'?'E.g., MASON':'E.g., 201345';
        if(name==='Left Name') { input.autocapitalize='characters'; input.spellcheck=false; }
        input.setAttribute('aria-label',`Item ${index+1} ${name}`); input.dataset.setProperty=name; input.value=state.properties[name] || '';
        field.append(caption,input); fields.append(field);
      }
      const note=document.createElement('p'); note.className='oc-set__note';
      note.textContent='One flat $7.99 fee per personalized cuff. Best balance: up to 6 letters + 6 digits. Maximum: 8 + 8. Character size: approximately 3.3 × 3.7 mm.';
      fields.append(note); personal.append(fields); row.append(personal);
    }
    syncBraceletPersonalization(row,state,index) {
      const fields=row.querySelector('[data-set-bracelet-personal-fields]');
      if(!fields) return;
      const active=this.isBraceletPersonalized(state);
      fields.hidden=!active;
      for(const input of fields.querySelectorAll('[data-set-property]')) input.disabled=!active;
      if(!active) {
        state.properties['Left Name']=''; state.properties['Right Number']='';
        for(const input of fields.querySelectorAll('[data-set-property]')) input.value='';
      }
    }
    renderArtwork(row,state,index) {
      const artwork=document.createElement('div'); artwork.className='oc-set__artwork'; artwork.dataset.setArtwork='';
      const uploadLabel=document.createElement('label'); uploadLabel.className='oc-set__field is-wide';
      const uploadCaption=document.createElement('span'); uploadCaption.textContent='Upload Reference Artwork · Optional';
      const upload=document.createElement('input'); upload.type='file'; upload.accept='.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp'; upload.dataset.setArtworkFile='';
      upload.setAttribute('aria-label',`Item ${index+1} Upload Reference Artwork`);
      uploadLabel.append(uploadCaption,upload); artwork.append(uploadLabel);
      const filename=document.createElement('p'); filename.className='oc-set__filename'; filename.dataset.setArtworkName=''; artwork.append(filename);
      const notesLabel=document.createElement('label'); notesLabel.className='oc-set__field is-wide';
      const notesCaption=document.createElement('span'); notesCaption.textContent='Design Notes · Optional';
      const notes=document.createElement('textarea'); notes.rows=2; notes.maxLength=500; notes.dataset.setNotes=''; notes.value=state.notes || '';
      notes.setAttribute('aria-label',`Item ${index+1} Design Notes`); notesLabel.append(notesCaption,notes); artwork.append(notesLabel);
      const rightsLabel=document.createElement('label'); rightsLabel.className='oc-set__rights'; rightsLabel.dataset.setArtworkRightsRow='';
      const rights=document.createElement('input'); rights.type='checkbox'; rights.checked=state.rights; rights.dataset.setArtworkRights='';
      const rightsText=document.createElement('span'); rightsText.textContent='I own or have permission to use this artwork.';
      rightsLabel.append(rights,rightsText); artwork.append(rightsLabel);
      const proof=document.createElement('p'); proof.className='oc-set__note'; proof.textContent='We’ll send you an artwork proof for approval before production.'; artwork.append(proof);
      row.append(artwork);
    }
    syncArtwork(row,state,index) {
      const custom=this.isCustom(state);
      const artwork=row.querySelector('[data-set-artwork]');
      if(!artwork) return;
      artwork.hidden=!custom;
      const upload=artwork.querySelector('[data-set-artwork-file]');
      const notes=artwork.querySelector('[data-set-notes]');
      const rights=artwork.querySelector('[data-set-artwork-rights]');
      const rightsRow=artwork.querySelector('[data-set-artwork-rights-row]');
      upload.disabled=!custom; notes.disabled=!custom;
      rights.checked=state.rights;
      rights.disabled=!custom || !state.artwork; rights.required=!!(custom && state.artwork);
      rightsRow.hidden=!state.artwork;
      artwork.querySelector('[data-set-artwork-name]').textContent=state.artwork ? `Selected: ${state.artwork.name}` : '';
      if(!custom) { state.artwork=null; state.notes=''; state.rights=false; upload.value=''; notes.value=''; rights.checked=false; }
      if(custom && state.artwork) {
        const typeOk=/\.(jpe?g|png|webp)$/i.test(state.artwork.name) && /^image\/(jpeg|png|webp)$/.test(state.artwork.type);
        upload.setCustomValidity(!typeOk ? 'Please choose a JPG, PNG or WEBP image.' : state.artwork.size>10*1024*1024 ? 'Please choose an image smaller than 10 MB.' : '');
      } else upload.setCustomValidity('');
      notes.setCustomValidity(custom && /<[!/A-Za-z]/.test(state.notes) ? 'Use plain-text design notes without HTML tags.' : '');
      upload.setAttribute('aria-label',`Item ${index+1} Upload Reference Artwork`);
    }
    prices(count) {
      if (count===1 || !this.model.mixed || count>20) return [this.primaryVariant.price];
      this.ensureUnits(count);
      return this.units.slice(0,count).map(state => this.model.variants.find(v=>v.id===state.id)?.price ?? this.primaryVariant.price);
    }
    personalizationFeeCount(count) {
      if(!this.model.fiveVerse) return 0;
      if(count===1 || !this.usesMixedItems()) return this.purchase.ocFiveVerse?.personalized?.() ? 1 : 0;
      return this.units.slice(0,count).filter((state)=>state.fiveVerse.personalized).length;
    }
    syncPrices() {
      for(const card of this.cards) {
        const tier=Number(card.dataset.setCard), count=tier===3 ? Math.max(3,this.quantity) : tier;
        const quote=OCSetCore.quote(this.prices(count),this.model.eligible,this.model.currency,count,this.model.fixedOnly,this.model.twoRate,this.model.threeRate);
        const serviceTotal=this.personalizationFeeCount(count)*(this.model.nameServicePrice || 799);
        card.querySelector('[data-set-total]').textContent=this.money(quote.total+serviceTotal);
        const original=card.querySelector('[data-set-original]'); original.textContent=this.money(quote.original+serviceTotal); original.hidden=!quote.saved;
        const saving=card.querySelector('[data-set-saving]');
        saving.textContent=`Save ${this.money(quote.saved)} · ${quote.rate || `~${quote.percent}`}% off`;
        saving.hidden=!quote.saved;
        const badge=card.querySelector('[data-set-badge]'); badge.hidden=!quote.saved;
        badge.textContent=quote.rate ? `SAVE ${quote.rate}%` : `SAVE ${this.money(quote.saved)} · ~${quote.percent}% OFF`;
        if(tier===3) card.querySelector('.oc-set__title').firstChild.textContent=this.model.giraffe ? (count===3 ? '3+ BAGS' : `${count} BAGS`) : (count===3 ? '3+ PIECES' : `${count} PIECES`);
      }
      const note=this.querySelector('[data-set-note]');
      note.textContent=this.model.eligible ? 'Set totals shown for this selection. Savings apply in cart; offers do not stack. Other cart items may change savings.' : 'Choose each item your way. Your total is confirmed in cart.';
      if(this.model.fixedOnly) note.textContent=this.model.timeline ? 'Discounts apply automatically in cart. Offers do not stack.' : `Buy 1: save $5 · Buy 2: ${this.model.twoRate}% off · Buy 3+: ${this.model.threeRate}% off. Applied automatically in cart; offers do not stack.`;
      if(this.model.fiveVerse) note.textContent+=' Optional name embossing is $7.99 per personalized bracelet and is not discounted.';
      if(this.model.fiveVerse && !this.model.raisedDome) note.textContent='Savings apply automatically in cart. Offers do not stack. Optional name embossing (+$7.99) is excluded from discounts.';
      if(this.model.eligible && this.model.currency!=='USD') note.textContent=this.model.fixedOnly ? `Buy 1 offer confirmed in cart · Buy 2: ${this.model.twoRate}% off · Buy 3+: ${this.model.threeRate}% off.` : 'Percentage set savings shown. Any additional eligible single-item savings are confirmed in cart.';
    }
    items(properties={}) {
      if(!this.usesMixedItems()) {
        let singleProperties={...properties};
        if(this.model.giraffe) {
          const strapLength=singleProperties['Requested Strap Length — Bag 1'];
          for(const key of Object.keys(singleProperties)) if(/^Requested Strap Length — Bag \d+$/.test(key)) delete singleProperties[key];
          if(strapLength) singleProperties['Requested Strap Length']=strapLength;
        }
        const badge=this.model.fiveVerse ? (this.purchase.ocFiveVerse?.selectedBadge?.() || this.model.badges[0]) : null;
        const variant=this.model.fiveVerse ? this.fiveVerseVariant(this.primaryId.value,badge) : null;
        return [{id:Number(variant?.id || this.primaryId.value),quantity:this.quantity,properties:singleProperties}];
      }
      this.ensureUnits(this.quantity);
      const personalized=!!this.originalLuggage?.personalized?.();
      return this.units.slice(0,this.quantity).map((state,index)=>{
        let unitProperties={...properties};
        if(this.model.giraffe) {
          const strapLength=unitProperties[`Requested Strap Length — Bag ${index+1}`];
          for(const key of Object.keys(unitProperties)) if(/^Requested Strap Length — Bag \d+$/.test(key)) delete unitProperties[key];
          if(strapLength) unitProperties['Requested Strap Length']=strapLength;
        }
        if(this.model.luggage) {
          for(const key of ['Item','Center Design','Back Personalization','Name','Phone Number','Design Notes','Artwork Reference']) delete unitProperties[key];
          unitProperties.Item=`Item ${index+1} of ${this.quantity}`;
          unitProperties['Center Design']=state.options.find(value => /mountain cross|custom center artwork/i.test(value)) || state.options[0];
          unitProperties['Back Personalization']=personalized ? 'Name and/or Phone Number (Free)' : 'No Personalization';
          if(personalized) for(const [key,value] of Object.entries(state.properties)) if(value.trim()) unitProperties[key]=value.replace(/[\r\n\t]/g,' ').trim().slice(0,20);
          if(this.isCustom(state) && state.notes.trim()) unitProperties['Design Notes']=state.notes.trim().slice(0,500);
        } else if(this.model.customCenter) {
          for(const key of ['Item','Center Design','Personalization','Design Notes','Artwork Reference','Left Name','Right Number']) delete unitProperties[key];
          unitProperties.Item=`Item ${index+1} of ${this.quantity}`;
          if(this.isBraceletPersonalized(state)) {
            for(const key of ['Left Name','Right Number']) {
              const value=(state.properties[key] || '').trim();
              if(value) unitProperties[key]=value.slice(0,8);
            }
          }
        } else if(this.model.fiveVerse) {
          for(const key of ['Item','Badge Style','Metal Color','Personalization','Name or Initials','_Badge Internal ID']) delete unitProperties[key];
          unitProperties.Item=`Item ${index+1} of ${this.quantity}`;
          unitProperties['Badge Style']=state.fiveVerse.badge.style;
          unitProperties['Metal Color']=state.fiveVerse.badge.metal;
          if(state.fiveVerse.badge.internalId) unitProperties['_Badge Internal ID']=state.fiveVerse.badge.internalId;
          unitProperties.Personalization=state.fiveVerse.personalized ? 'Add Name Embossing (+$7.99)' : 'No Personalization';
          if(state.fiveVerse.personalized && state.fiveVerse.name.trim()) unitProperties['Name or Initials']=state.fiveVerse.name.trim();
        }
        const variant=this.model.fiveVerse ? this.fiveVerseVariant(state.id,state.fiveVerse.badge) : null;
        return {id:Number(variant?.id || state.id),quantity:1,properties:unitProperties};
      });
    }
    cartItems(formData) {
      const properties={};
      for(const [key,value] of formData.entries()) {
        const match=key.match(/^properties\[(.+)\]$/);
        if(match && typeof value==='string' && value.trim()) properties[match[1]]=value.trim();
      }
      return this.items(properties);
    }
    usesFiveVerseCart() { return !!this.model.fiveVerse; }
    fiveVerseSelections(formData) {
      const baseProperties={};
      for(const [key,value] of formData.entries()) {
        const match=key.match(/^properties\[(.+)\]$/);
        if(match && typeof value==='string' && value.trim()) baseProperties[match[1]]=value.trim();
      }
      for(const key of ['Item','Badge Style','Metal Color','Personalization','Name or Initials','_Badge Internal ID']) delete baseProperties[key];
      if(this.usesMixedItems()) {
        return this.units.slice(0,this.quantity).map((state,index)=>({
          id:Number(this.fiveVerseVariant(state.id,state.fiveVerse.badge)?.id || state.id),
          personalized:!!state.fiveVerse.personalized,
          name:state.fiveVerse.name.trim(),
          properties:{
            ...baseProperties,
            Item:`Item ${index+1} of ${this.quantity}`,
            'Badge Style':state.fiveVerse.badge.style,
            'Metal Color':state.fiveVerse.badge.metal,
            ...(state.fiveVerse.badge.internalId ? {'_Badge Internal ID':state.fiveVerse.badge.internalId} : {}),
            Personalization:state.fiveVerse.personalized ? 'Add Name Embossing (+$7.99)' : 'No Personalization',
            ...(state.fiveVerse.personalized ? {'Name or Initials':state.fiveVerse.name.trim()} : {})
          }
        }));
      }
      const controls=this.purchase.ocFiveVerse;
      const badge=controls?.selectedBadge?.() || this.model.badges[0];
      const personalized=!!controls?.personalized?.();
      const name=controls?.name?.() || '';
      return [{
        id:Number(this.fiveVerseVariant(this.primaryId.value,badge)?.id || this.primaryId.value), personalized, name,
        properties:{
          ...baseProperties,
          'Badge Style':badge.style,
          'Metal Color':badge.metal,
          ...(badge.internalId ? {'_Badge Internal ID':badge.internalId} : {}),
          Personalization:personalized ? 'Add Name Embossing (+$7.99)' : 'No Personalization',
          ...(personalized ? {'Name or Initials':name} : {})
        }
      }];
    }
    fiveVerseCartBodies(formData) {
      const sellingPlan=formData.get('selling_plan');
      return this.fiveVerseSelections(formData).map((selection)=>{
        const parent={id:selection.id,quantity:1,properties:selection.properties};
        if(sellingPlan) parent.selling_plan=sellingPlan;
        const items=[parent];
        if(selection.personalized) {
          items.push({
            id:Number(this.model.nameServiceVariant),
            quantity:1,
            parent_id:selection.id,
            properties:{'Name or Initials':selection.name,'_Service':'Name Embossing Service'}
          });
        }
        return {items};
      });
    }
    cartFormData(formData) {
      const items=this.cartItems(formData);
      return items.map((item,index)=>{
        const itemForm=new FormData();
        itemForm.set('id',String(item.id));
        itemForm.set('quantity','1');
        for(const [key,value] of Object.entries(item.properties || {})) if(value !== '') itemForm.set(`properties[${key}]`,value);
        const state=this.units[index];
        if(this.model.luggage && this.isCustom(state) && state.artwork) itemForm.set('properties[Artwork Reference]',state.artwork,state.artwork.name);
        const sellingPlan=formData.get('selling_plan');
        if(sellingPlan) itemForm.set('selling_plan',sellingPlan);
        return itemForm;
      });
    }
    customizationError() {
      if(this.model.fiveVerse) {
        const nameValidator=window.OROCUFFFiveVerse?.validName;
        if(!this.usesMixedItems()) {
          const controls=this.purchase.ocFiveVerse;
          const controlError=controls?.validate?.() || '';
          if(controlError) return controlError;
        } else {
          for(let index=0;index<this.quantity;index++) {
            const state=this.units[index];
            if(!state.fiveVerse.personalized) continue;
            if(!this.model.nameServiceAvailable || !this.model.nameServiceVariant) return 'Name embossing is not available until the $7.99 service is confirmed in Shopify.';
            if(!nameValidator?.(state.fiveVerse.name)) return `Enter 1–12 valid characters for Item ${index+1}. Use A–Z, 0–9 and single spaces in any order.`;
          }
        }
      }
      if(!(this.model.luggage || this.model.customCenter) || !this.usesMixedItems()) return '';
      const personalized=!!this.originalLuggage?.personalized?.();
      for(let index=0; index<this.quantity; index++) {
        const state=this.units[index], name=(state.properties.Name || '').trim(), phone=(state.properties['Phone Number'] || '').trim();
        if(this.model.luggage && personalized && !name && !phone) return `Enter a name, a phone number, or both for Item ${index+1}, or choose Keep the Back Blank.`;
        if(this.model.luggage && personalized && name && !/^[A-Za-z ]+$/.test(name)) return `Use English letters and spaces for Item ${index+1} Name.`;
        if(this.model.luggage && personalized && phone && (!/^[0-9+() .-]+$/.test(phone) || !/[0-9]/.test(phone))) return `Enter a valid phone number for Item ${index+1}.`;
        const left=(state.properties['Left Name'] || '').trim(), right=(state.properties['Right Number'] || '').trim();
        if(this.model.customCenter && this.isBraceletPersonalized(state) && !left && !right) return `Enter a left name, a right number, or both for Item ${index+1}, or choose No Personalization.`;
        if(this.model.customCenter && this.isBraceletPersonalized(state) && left && !/^[A-Z]{1,8}$/.test(left)) return `Use 1–8 uppercase English letters (A–Z) for Item ${index+1} Left Name.`;
        if(this.model.customCenter && this.isBraceletPersonalized(state) && right && !/^[0-9]{1,8}$/.test(right)) return `Use 1–8 digits (0–9) for Item ${index+1} Right Number.`;
        if(this.isCustom(state) && /<[!/A-Za-z]/.test(state.notes)) return `Use plain-text Design Notes for Item ${index+1}.`;
        if(this.isCustom(state) && state.artwork) {
          if(!/\.(jpe?g|png|webp)$/i.test(state.artwork.name) || !/^image\/(jpeg|png|webp)$/.test(state.artwork.type)) return `Choose a JPG, PNG or WEBP image for Item ${index+1}.`;
          if(state.artwork.size>10*1024*1024) return `Choose an image smaller than 10 MB for Item ${index+1}.`;
          if(!state.rights) return `Confirm artwork permission for Item ${index+1}.`;
        }
      }
      return '';
    }
    validate() {
      this.validationMessage=this.customizationError() || OCSetCore.validate(this.items(),this.model.variants);
      this.syncNotice();
      return !this.validationMessage;
    }
    syncNotice() {
      this.validationMessage=this.customizationError() || OCSetCore.validate(this.items(),this.model.variants);
      const notice=this.querySelector('[data-set-notice]'); notice.textContent=this.validationMessage; notice.hidden=!this.validationMessage;
    }
    syncSticky() {
      for(const sticky of document.querySelectorAll('sticky-cart')) {
        if(sticky.dataset.form!==this.dataset.formId) continue;
        const quantity=sticky.querySelector('input[name="quantity"]'); if(quantity) quantity.value=String(this.quantity);
        const price=sticky.querySelector('.sticky-cart__price-media');
        const variants=sticky.querySelector('.sticky-cart__variant-selects');
        if(variants) { variants.hidden=this.usesMixedItems(); variants.dataset.setHidden=String(this.usesMixedItems()); }
        const basePrice=sticky.querySelector('.price--sticky');
        if(basePrice) basePrice.dataset.setHidden='true';
        const mobileVariant=sticky.querySelector('.stick-cart__variant-select-mobile-button');
        if(mobileVariant) mobileVariant.dataset.setHidden=String(this.usesMixedItems());
        let total=sticky.querySelector('[data-set-sticky-total]');
        if(!total && price) { total=document.createElement('span'); total.className='oc-set-sticky-total'; total.dataset.setStickyTotal=''; price.append(total); }
        if(total) {
          const quote=OCSetCore.quote(this.prices(this.quantity),this.model.eligible,this.model.currency,this.quantity,this.model.fixedOnly,this.model.twoRate,this.model.threeRate);
          const serviceTotal=this.personalizationFeeCount(this.quantity)*(this.model.nameServicePrice || 799);
          total.textContent=this.quantity===1 ? this.money(quote.total+serviceTotal) : `${this.quantity} items · ${this.money(quote.total+serviceTotal)}`; total.hidden=false;
        }
      }
    }
  });
}
})();
